// Which of the vehicles standing inside an area somebody needs to do something about.
//
// A list of everything on site is a log. What a yard actually wants is a worklist: of the forty
// lorries inside the fence, which three are the problem. The page used to leave that to the reader,
// who had to notice that one number in a column was larger than the others.
//
// The rules are not new ones invented for this screen. They are the rules the product already acts
// on elsewhere, read from the same configuration, so what this page calls a problem and what the
// Exceptions screen raises an exception for are the same thing. A screen that quietly disagrees
// with the exception list is worse than one that says nothing: somebody acts on the wrong number
// and then stops trusting both.

/**
 * Why this vehicle needs attention, or null if it does not.
 *
 * `area` is the geofence it is standing in, `insideMs` how long it has been there, `stale` whether
 * its device has gone quiet, `trip` its open trip if it has one, and `config` the tenant's rules.
 */
export const attentionOf = ({
  areaType, insideMs, stale, trip, byReader,
}, config) => {
  // A tracker that has stopped talking is a fault. A lorry identified by readers, sitting between
  // reads, is the plant working exactly as intended - it has no tracker to go quiet, which is the
  // point of fitting a reader instead. The two arrive here looking identical, and calling both "not
  // reporting" buries the real one under a dozen that are fine.
  if (stale && !byReader) {
    return { level: 'warning', label: 'Not reporting', detail: 'Geometrically inside, but the device has gone quiet.' };
  }
  // On site with no paperwork open against it. The product already raises this as an exception;
  // here it is the difference between a neutral "No trip" chip and something to go and look at.
  if (areaType === 'PLANT' && !trip) {
    return { level: 'error', label: 'On site without a trip', detail: 'Inside the plant with no open trip to account for it.' };
  }
  // Waited outside longer than the plant allows before calling a lorry in. Same threshold the
  // call-in exception uses, so the two cannot disagree.
  const threshold = config?.parkingDwellMs || 0;
  if (areaType === 'PARKING' && threshold > 0 && insideMs != null && insideMs > threshold) {
    return { level: 'error', label: 'Waiting beyond the call-in time', detail: 'Outside longer than this plant allows before a lorry is called in.' };
  }
  return null;
};

/** The worst thing happening in an area, for a summary that should lead with its worst news. */
export const worstOf = (rows) => rows.reduce((held, row) => {
  if (row.attention?.level === 'error') return 'error';
  if (row.attention?.level === 'warning' && held !== 'error') return 'warning';
  return held;
}, null);

/** How a count of problems reads when there are none, which is the usual and best case. */
export const attentionText = (count) => (count
  ? `${count} need${count === 1 ? 's' : ''} attention`
  : 'Nothing needs attention');
